import React, { useEffect, useState } from "react";
import {
  Typography,
  Skeleton,
  Table,
  Tabs,
  Button,
  Tag,
  Image,
  Empty,
} from "antd";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import { FiBookOpen, FiClock } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Get } from "../../../config/api/get";
import { ASSOCIATE_PORTAL, UPLOADS_URL } from "../../../config/constants/api";
import AssociateLayout from "../../../components/AssociateLayout";
import "../dashboard/associate-dashboard.css";

const { Title } = Typography;

function lessonCount(course, lectures) {
  if (Array.isArray(lectures) && lectures.length > 0) {
    return lectures.length;
  }
  if (Array.isArray(course?.lectures)) {
    return course.lectures.length;
  }
  return 0;
}

function AssociateCourseDetails() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [assets, setAssets] = useState([]);
  const [activeTab, setActiveTab] = useState("lectures");

  useEffect(() => {
    loadCourse();
    loadLectures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    if (activeTab === "assets" && assets.length === 0) {
      loadAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, courseId]);

  const loadCourse = async () => {
    setLoading(true);
    try {
      const res = await Get(ASSOCIATE_PORTAL.course + courseId, token);
      if (res?.status) {
        setCourse(res.data?.course);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLectures = async () => {
    const res = await Get(
      `${ASSOCIATE_PORTAL.lectures}${courseId}/lectures`,
      token,
      { page: "1", limit: "100" }
    );
    if (res?.status) {
      setLectures(res.data?.docs || []);
    }
  };

  const loadAssets = async () => {
    const res = await Get(
      `${ASSOCIATE_PORTAL.assets}${courseId}/assets`,
      token
    );
    if (res?.status) {
      setAssets(res.data?.assets || []);
    }
  };

  const lectureColumns = [
    {
      title: "S. No.",
      key: "index",
      width: 80,
      render: (_, __, index) => (index < 9 ? "0" : "") + (index + 1),
    },
    {
      title: "Lecture #",
      dataIndex: "lectureNo",
      key: "lectureNo",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <FaEye
          style={{ fontSize: 16, color: "#1565c0", cursor: "pointer" }}
          title="View lecture"
          onClick={() => navigate(`/associate/lectures/${record._id}`)}
        />
      ),
    },
  ];

  const assetColumns = [
    {
      title: "S. No.",
      key: "index",
      width: 80,
      render: (_, __, index) => (index < 9 ? "0" : "") + (index + 1),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Open",
      key: "open",
      render: (_, record) => (
        <Button
          type="link"
          href={`${UPLOADS_URL}/${record.fileUrl}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View file
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: "lectures",
      label: `Lectures (${lectures.length})`,
      children:
        lectures.length === 0 ? (
          <Empty description="No lectures in this course" />
        ) : (
          <Table
            rowKey="_id"
            columns={lectureColumns}
            dataSource={lectures}
            pagination={false}
            size="middle"
          />
        ),
    },
    {
      key: "assets",
      label: `Resources (${assets.length})`,
      children:
        assets.length === 0 ? (
          <Empty description="No resources for this course" />
        ) : (
          <Table
            rowKey="_id"
            columns={assetColumns}
            dataSource={assets}
            pagination={false}
            size="middle"
          />
        ),
    },
  ];

  const lessons = lessonCount(course, lectures);
  const duration = course?.duration?.trim() || "—";

  return (
    <AssociateLayout footer={false}>
      <div className="associate-portal-page">
        <div className="associate-portal-page__inner">
          <button
            type="button"
            className="associate-back-link fontFamily1"
            onClick={() => navigate("/associate/dashboard")}
          >
            <FaArrowLeft aria-hidden />
            Back to my courses
          </button>

          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : !course ? (
            <Empty description="Course not found" />
          ) : (
            <>
              <section className="associate-course-hero">
                <div className="associate-course-hero__media">
                  {course.image ? (
                    <Image
                      src={`${UPLOADS_URL}/${course.image}`}
                      alt={course.title}
                      preview={false}
                    />
                  ) : null}
                </div>
                <div className="associate-course-hero__content">
                  <Title level={2} className="associate-course-hero__title fontFamily1">
                    {course.title}
                  </Title>
                  {course.courseCode ? (
                    <span className="associate-course-hero__code fontFamily1">
                      {course.courseCode}
                    </span>
                  ) : null}
                  {course.description ? (
                    <p className="associate-course-hero__desc fontFamily1">
                      {course.description}
                    </p>
                  ) : null}
                  <div className="associate-course-card__meta fontFamily1">
                    <span>
                      <FiBookOpen aria-hidden />
                      {lessons} Lesson{lessons === 1 ? "" : "s"}
                    </span>
                    <span>
                      <FiClock aria-hidden />
                      {duration}
                    </span>
                  </div>
                </div>
              </section>

              <section className="associate-course-panel">
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={tabItems}
                />
              </section>
            </>
          )}
        </div>
      </div>
    </AssociateLayout>
  );
}

export default AssociateCourseDetails;
